/**
 * Bot Detection Wrapper Component
 * Wraps registration form with bot detection capabilities
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Alert,
  CircularProgress,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  LinearProgress
} from '@mui/material';
import {
  Security as SecurityIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import BotDetectionService, { BotDetectionResult } from '../../services/botDetectionService';

interface BotDetectionWrapperProps {
  children: React.ReactNode;
  onBotDetected?: (result: BotDetectionResult) => void;
  onHumanVerified?: () => void;
  showDetectionStatus?: boolean;
  recaptchaSiteKey?: string;
}

const BotDetectionWrapper: React.FC<BotDetectionWrapperProps> = ({
  children,
  onBotDetected,
  onHumanVerified,
  showDetectionStatus = true,
  recaptchaSiteKey
}) => {
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [detectionResult, setDetectionResult] = useState<BotDetectionResult | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [recaptchaLoaded, setRecaptchaLoaded] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
  const recaptchaRef = useRef<HTMLDivElement>(null);
  const botDetectionService = BotDetectionService.getInstance();

  // Load reCAPTCHA if site key is provided
  useEffect(() => {
    if (recaptchaSiteKey && !recaptchaLoaded) {
      loadRecaptcha();
    }
  }, [recaptchaSiteKey, recaptchaLoaded]);

  const loadRecaptcha = () => {
    if (typeof window === 'undefined' || !recaptchaSiteKey) return;

    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${recaptchaSiteKey}`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      setRecaptchaLoaded(true);
    };
    document.head.appendChild(script);
  };

  const executeRecaptcha = async (): Promise<string | null> => {
    if (!recaptchaSiteKey || !recaptchaLoaded || typeof window === 'undefined') {
      return null;
    }

    try {
      return new Promise((resolve) => {
        try {
          (window as any).grecaptcha.ready(() => {
            (window as any).grecaptcha.execute(recaptchaSiteKey, { action: 'register' })
              .then((token: string) => {
                resolve(token);
              })
              .catch((error: any) => {
                console.warn('reCAPTCHA execution failed:', error);
                resolve(null);
              });
          });
        } catch (error) {
          console.warn('reCAPTCHA ready failed:', error);
          resolve(null);
        }
      });
    } catch (error) {
      console.error('reCAPTCHA execution failed:', error);
      return null;
    }
  };

  const handleFormSubmit = async (formData: any): Promise<void> => {
    setIsAnalyzing(true);
    
    try {
      // Execute reCAPTCHA if available
      const token = await executeRecaptcha().catch(() => null);
      setRecaptchaToken(token || null);

      // Perform bot detection
      const result = await botDetectionService.detectBot(formData, token || undefined);
      setDetectionResult(result);

      if (result.isBot) {
        console.warn('🤖 Bot detected:', result);
        onBotDetected?.(result);
        // Throw error to prevent registration from proceeding
        throw new Error(`Bot detected (confidence: ${result.confidence}%)`);
      } else {
        console.log('✅ Human verified:', result);
        onHumanVerified?.();
        // Resolve promise to allow registration to proceed
      }
    } catch (error: any) {
      console.error('Bot detection failed:', error);
      // If it's a bot detection error, re-throw it
      if (error.message?.includes('Bot detected')) {
        throw error;
      }
      // If detection fails due to technical issues, allow the form to proceed
      onHumanVerified?.();
    } finally {
      setIsAnalyzing(false);
    }
  };

  const getRiskLevelColor = (riskLevel: string) => {
    switch (riskLevel) {
      case 'critical': return 'error';
      case 'high': return 'warning';
      case 'medium': return 'info';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  const getRiskLevelIcon = (riskLevel: string) => {
    switch (riskLevel) {
      case 'critical': return <ErrorIcon />;
      case 'high': return <WarningIcon />;
      case 'medium': return <WarningIcon />;
      case 'low': return <CheckCircleIcon />;
      default: return <SecurityIcon />;
    }
  };

  // Clone children and inject bot detection
  const childrenWithBotDetection = React.Children.map(children, (child) => {
    if (React.isValidElement(child)) {
      return React.cloneElement(child, {
        onSubmit: handleFormSubmit
      } as any);
    }
    return child;
  });

  return (
    <Box>
      {/* Bot Detection Status */}
      {showDetectionStatus && (
        <Box sx={{ mb: 2 }}>
          {isAnalyzing && (
            <Alert 
              severity="info" 
              icon={<CircularProgress size={20} />}
              sx={{ mb: 1 }}
            >
              <Typography variant="body2">
                Analyzing behavior for bot detection...
              </Typography>
              <LinearProgress sx={{ mt: 1 }} />
            </Alert>
          )}

          {detectionResult && (
            <Alert 
              severity={detectionResult.isBot ? 'warning' : 'success'}
              icon={getRiskLevelIcon(detectionResult.riskLevel)}
              action={
                <Button 
                  size="small" 
                  onClick={() => setShowDetails(true)}
                  color="inherit"
                >
                  Details
                </Button>
              }
              sx={{ mb: 1 }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2">
                  {detectionResult.isBot 
                    ? `Bot detected (${detectionResult.confidence}% confidence)`
                    : `Human verified (${detectionResult.confidence}% confidence)`
                  }
                </Typography>
                <Chip 
                  label={detectionResult.riskLevel}
                  color={getRiskLevelColor(detectionResult.riskLevel) as any}
                  size="small"
                />
              </Box>
            </Alert>
          )}

          {recaptchaSiteKey && (
            <Alert severity="info" sx={{ mb: 1 }}>
              <Typography variant="body2">
                {recaptchaLoaded 
                  ? '🛡️ reCAPTCHA protection active'
                  : '🔄 Loading reCAPTCHA protection...'
                }
              </Typography>
            </Alert>
          )}
        </Box>
      )}

      {/* Form with Bot Detection */}
      {childrenWithBotDetection}

      {/* Bot Detection Details Dialog */}
      <Dialog 
        open={showDetails} 
        onClose={() => setShowDetails(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SecurityIcon />
            Bot Detection Analysis
          </Box>
        </DialogTitle>
        <DialogContent>
          {detectionResult && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Detection Result
              </Typography>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="textSecondary">
                  Confidence: {detectionResult.confidence}%
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Risk Level: {detectionResult.riskLevel}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Timestamp: {detectionResult.timestamp.toLocaleString()}
                </Typography>
              </Box>

              <Typography variant="h6" gutterBottom>
                Detection Reasons
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                {detectionResult.reasons.map((reason, index) => (
                  <Chip 
                    key={index} 
                    label={reason} 
                    color="warning" 
                    size="small"
                  />
                ))}
              </Box>

              {recaptchaToken && (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    reCAPTCHA Score
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Token: {recaptchaToken.substring(0, 20)}...
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDetails(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BotDetectionWrapper;
